<template>
  <LoginWithPhoneNumberModal
    v-model:is-open="showAuthModal"
    v-model:step="loginStep"
    :loading="isSubmitting || isSendingOtpCode"
    :error="error"
    :countdown-seconds="countdown ?? undefined"
    @submit-phone-number="sendOtpCode"
    @submit-otp="loginWithOtp"
  >
    <template #authOptions>
      <slot
        name="authOptions"
        :loading="isSubmitting || isSendingOtpCode"
        @sessionResponse="onSessionResponse"
      />
    </template>
  </LoginWithPhoneNumberModal>
</template>

<script setup lang="ts">
import { ref, watch } from "vue";
import { useAwaitAction } from "@/composables/use-await-action";
import { useChannelStore } from "@/stores/channel.store";

import {
  EventCategory,
  TrackEventEnum,
} from "@/services/tracking/tracking-types";
import { useTrackingStore } from "@/stores/tracking.store";
import { useJustOneOpenModal } from "@/composables/buses/use-modal-bus";
import type { ResultOf } from "@graphql-typed-document-node/core";
import {
  AuthChannelType,
  type SessionResponse,
  type ValidatedUserIdentifier,
} from "@/generated/graphql";
import { useAuthentication } from "@/composables/auth/use-authentication";
import { useAuthSession } from "@/composables/auth/use-auth-session";
import {
  AsyncFlowMessageType,
  useAuthAsyncFlowManager,
} from "@/composables/auth/use-auth-async-flow-manager";
import { useCountdown } from "@vueuse/core";
import type { UserStoreMe } from "@/stores/user.store";
import { useI18n } from "vue-i18n";
import LoginWithPhoneNumberModal from "@/components/channel/auth/common/login/login-with-phone-number-modal.vue";

const LOGIN_DEBOUNCE_DELAY = 150;

const {
  remaining: countdown,
  start: startCountdown,
  reset: resetCountdown,
  // initially set to 0
} = useCountdown(0, {
  onComplete() {},
  onTick() {},
});

const showAuthModal = defineModel<boolean>("showAuthModal", {
  required: true,
});
useJustOneOpenModal(showAuthModal);

const emit = defineEmits<{
  signup: [
    validatedIdentifiers: ValidatedUserIdentifier[],
    authChannelType: AuthChannelType,
    sessionToken: string,
  ];
  loggedIn: [user: UserStoreMe];
}>();

const channelStore = useChannelStore();
const trackingService = useTrackingStore();
const { authenticate, verifyAuthentication } = useAuthentication();
const { handleSessionResponse } = useAuthSession({ mode: "inApp" });
const { t } = useI18n();

const loginStep = ref<"phoneNumber" | "otp">("phoneNumber");
const state = ref<string | null>(null);
const error = ref<string | null>(null);

watch(showAuthModal, (isVisible, prevVisible) => {
  if (isVisible && !prevVisible) {
    error.value = null;
  }
});

const { watchAsyncFlowMessage, sendCloseWindowMessage } =
  useAuthAsyncFlowManager(window.opener ?? window);

const { loading: isSendingOtpCode, handler: sendOtpCode } = useAwaitAction(
  async (phoneNumber: string) => {
    error.value = null;

    const result = await authenticate({
      channelId: channelStore.channel!.id,
      authChannel: "otp" as AuthChannelType,
      request: {
        channel_id: channelStore.channel!.id,
        redirect_uri: phoneNumber,
      },
    });

    const response = result?.data?.authenticate;
    if (result?.error || response === undefined) {
      return (error.value =
        result?.error?.toString() ??
        t("channel.auth.identityStrategy.login.missingResponse"));
    }

    if ("error" in response) {
      switch (response.__typename) {
        case "InvalidInitiateAuthRequestError":
          error.value =
            response.message ??
            t("channel.auth.identityStrategy.login.invalidPhoneNumber");
          break;
        case "RateLimitedPhoneNumberError":
          if (loginStep.value === "phoneNumber") {
            error.value = t(
              "channel.auth.identityStrategy.login.rateLimitedWait",
              {
                seconds: Math.floor(response.countdown_milliseconds / 1000),
              },
            );
          }
          resetCountdown();
          startCountdown(Math.floor(response.countdown_milliseconds / 1000));
          break;
        default: {
          // Exhaustive narrow: `response` is `never` here in a well-typed
          // world. Cast via `unknown` so the schema can grow new error
          // variants without crashing this branch at runtime.
          const fallbackError =
            (response as unknown as { error?: string }).error ??
            "Unknown error";
          error.value = t("channel.auth.identityStrategy.login.genericError", {
            error: fallbackError,
          });
          break;
        }
      }
    } else {
      resetCountdown();
      startCountdown(Math.floor(response.countdown_milliseconds / 1000));

      loginStep.value = "otp";
      state.value = response.state;
    }
  },
  { debounce: LOGIN_DEBOUNCE_DELAY },
);

async function onSessionResponse(data: SessionResponse): Promise<boolean> {
  const outcome = await handleSessionResponse(data, data.auth_channel);

  if (outcome.status === "needs_signup") {
    emit(
      "signup",
      outcome.validatedIdentifiers,
      outcome.authChannelType,
      outcome.sessionToken,
    );
    return false;
  }

  showAuthModal.value = false;
  emit("loggedIn", outcome.user);
  return true;
}

function loginWithOtp({
  fullPhoneNumber,
  otp,
}: {
  fullPhoneNumber: string;
  otp: string;
}) {
  if (!state.value) {
    error.value = t("channel.auth.identityStrategy.login.noState");
    return false;
  }

  return login({
    code: otp,
    redirectUri: `${fullPhoneNumber}|||${state.value}`,
    authChannelType: AuthChannelType.Otp,
  });
}

const { loading: isSubmitting, handler: login } = useAwaitAction(
  async ({
    code,
    redirectUri,
    authChannelType,
  }: {
    code: string;
    redirectUri: string;
    authChannelType: AuthChannelType;
  }): Promise<boolean> => {
    error.value = null;

    const result = await verifyAuthentication({
      channelId: channelStore.channel!.id,
      authChannel: authChannelType,
      request: {
        channel_id: channelStore.channel!.id,
        code: code,
        redirect_uri: redirectUri,
      },
    });

    const data = result?.data?.verifyAuthentication;
    if (result?.error || data === undefined) {
      error.value =
        result?.error?.toString() ??
        t("channel.auth.identityStrategy.login.missingResponse");
      return false;
    }

    if ("error" in data) {
      switch (data.__typename) {
        case "InvalidAuthCodeError":
          error.value = t("channel.auth.identityStrategy.login.invalidOtpCode");
          trackingService.track({
            event: TrackEventEnum.LoginFailed,
            category: EventCategory.Identity,
            metadata: {
              error: "InvalidAuthCodeError",
              ...channelStore.gatingExperience,
            },
          });
          break;
        default: {
          // Exhaustive narrow: `data` is `never` here in a well-typed world.
          // Cast via `unknown` so a new error variant surfaces at runtime.
          const fallbackError =
            (data as unknown as { error?: string }).error ?? "unknown";
          error.value = `An error occurred (${fallbackError}). Please try again.`;
          break;
        }
      }
      return false;
    }

    return onSessionResponse(data as ResultOf<typeof data>);
  },
  { debounce: LOGIN_DEBOUNCE_DELAY },
);

watchAsyncFlowMessage(
  (data) => {
    switch (data.type) {
      case AsyncFlowMessageType.ValidateAuthenticationSuccess: {
        login({
          code: data.code,
          redirectUri: data.redirect_uri,
          // TODO: Extract this from a local storage key dependent on either the redirect uri or the state or code
          authChannelType: AuthChannelType.Oauth,
        });
        sendCloseWindowMessage();
        break;
      }
      case AsyncFlowMessageType.ValidateAuthenticationError: {
        error.value = data.error;
        break;
      }
    }
  },
  { fetchLastMessage: true },
);
</script>

<style scoped></style>
